import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHero } from '@/components/shared/PageHero';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { CAPTAIN, BARANGAY, BRAND } from '@/data/mockData';
import {
  ShieldCheck, Leaf, HeartPulse, GraduationCap, Trophy, Siren, FileCheck2,
  Quote, ArrowRight, Mail, Phone, Calendar,
} from 'lucide-react';

const ICONS = { ShieldCheck, Leaf, HeartPulse, GraduationCap, Trophy, Siren, FileCheck2 };

const Captain = () => {
  return (
    <div data-testid="captain-page">
      <PageHero
        eyebrow="Punong Barangay"
        title="Office of the Punong Barangay"
        subtitle="Public service priorities, office updates, and ways residents can engage with barangay leadership."
        breadcrumbs={[{ label: 'Office of the Punong Barangay' }]}
      />

      <section className="container-app -mt-12 relative z-10">
        <Card className="overflow-hidden border-0 shadow-elegant bg-card">
          <div className="grid lg:grid-cols-5 gap-0">
            <div className="lg:col-span-2 relative bg-gradient-to-br from-primary/10 via-background to-accent-soft p-8 flex items-center justify-center">
              <img
                src={BRAND.logoUrl}
                alt={`${BARANGAY.name} logo`}
                className="h-40 w-40 rounded-full bg-white p-2 shadow-lg-soft"
              />
            </div>
            <div className="lg:col-span-3 p-8 lg:p-10">
              <Badge className="bg-primary/10 text-primary hover:bg-primary/20">Term {CAPTAIN.term}</Badge>
              <h2 className="mt-4 font-display text-3xl lg:text-4xl font-bold text-primary-deep">Office of the Punong Barangay</h2>
              <p className="text-sm text-muted-foreground">{CAPTAIN.title}, {BARANGAY.name}</p>
              <p className="mt-5 text-foreground/85 leading-relaxed">
                This page is for official barangay governance updates, office priorities, and resident service coordination.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild className="bg-gradient-primary text-primary-foreground" data-testid="captain-contact-cta">
                  <Link to="/contact"><Mail className="mr-2 h-4 w-4" /> Send a message</Link>
                </Button>
                <Button asChild variant="outline" data-testid="captain-officials-cta">
                  <Link to="/officials">Meet the council <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* MESSAGE */}
      <section className="container-app section-padding">
        <Card className="relative overflow-hidden p-8 lg:p-12 border-0 bg-gradient-hero text-primary-foreground shadow-elegant">
          <Quote className="absolute -top-2 -left-2 h-32 w-32 opacity-10" />
          <div className="relative max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] backdrop-blur">
              Office Message
            </div>
            <p className="mt-6 font-display text-xl lg:text-2xl/relaxed font-medium tracking-tight">
              “{CAPTAIN.message}”
            </p>
            <div className="mt-6 text-sm opacity-90">— Office of the Punong Barangay</div>
          </div>
        </Card>
      </section>

      {/* PRIORITIES */}
      <section className="container-app section-padding">
        <SectionHeader
          eyebrow="Office Priorities"
          title="The 7 Pillars of Service"
          description="Every project, ordinance, and program is anchored on these seven priorities."
          align="center"
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CAPTAIN.priorities.map((p, i) => {
            const Icon = ICONS[p.icon] || ShieldCheck;
            return (
              <Card key={i} className="p-6 card-elevated border-0 group">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-primary text-primary-foreground shadow-md-soft group-hover:scale-110 transition">
                  <Icon className="h-5 w-5" />
                </div>
                <h4 className="mt-4 font-heading font-semibold text-lg">{p.title}</h4>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </Card>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="container-app pb-20">
        <Card className="p-8 lg:p-10 border-0 card-elevated bg-gradient-to-br from-accent-soft via-background to-primary/5">
          <div className="grid lg:grid-cols-3 gap-6 items-center">
            <div className="lg:col-span-2">
              <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Have something to share with Kapitan?</h3>
              <p className="mt-2 text-muted-foreground max-w-xl">Send a message, request a meeting, or join the next barangay assembly. Every voice in San Isidro matters.</p>
            </div>
            <div className="flex flex-col gap-3">
              <Button asChild className="w-full bg-gradient-primary text-primary-foreground" data-testid="captain-cta-contact">
                <Link to="/contact"><Mail className="mr-2 h-4 w-4" /> Contact us</Link>
              </Button>
              <Button asChild variant="outline" className="w-full" data-testid="captain-cta-events">
                <Link to="/programs"><Calendar className="mr-2 h-4 w-4" /> Upcoming events</Link>
              </Button>
              <a href={`tel:${BARANGAY.hotline}`} className="text-center text-sm font-medium text-primary hover:underline inline-flex items-center justify-center gap-1.5">
                <Phone className="h-4 w-4" /> Or call {BARANGAY.hotline}
              </a>
            </div>
          </div>
        </Card>
      </section>
    </div>
  );
};

export default Captain;
